import { useState } from 'react';
import { ArrowUpRight, Check, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useCompany } from '@/company/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { useAppCatalogLocale } from '@/i18n/context';
import { formatCount } from '@/i18n/format';
import pageCatalog, { type ConnectionsPageCopyKey } from '@/i18n/ns/connections-page';
import { WorkspaceIcon } from './icons';
import type { ScopedConnectionItem } from './connection-api';
import type { ConnectionAccessChoice } from './connection-read-access/api';
import { ConnectionPolicyFields, useConnectionPeople } from './connect-access/policy-fields';
import { ConnectionMark } from './your-connections/connection-mark';

type Step = 'choose' | 'prepare' | 'access';
/** Where the connection stands once the person continued to Meta (the page reads it from its hook). */
export type CustomerAppProgress =
  | { phase: 'waiting'; connectLink: string }
  | { phase: 'done'; item: ScopedConnectionItem };

// Meta's permission names, said in words; an unknown one is shown as Meta names it.
const SCOPE_WORDS: Record<string, ConnectionsPageCopyKey> = {
  ads_read: 'scopeAdsRead',
  ads_management: 'scopeAdsManagement',
  ads_mcp_management: 'scopeAdsMcp',
  business_management: 'scopeBusiness',
  pages_show_list: 'scopePages',
  pages_read_engagement: 'scopePageEngagement',
  instagram_basic: 'scopeInstagram',
  catalog_management: 'scopeCatalog',
};

const META_APPS_URL = 'https://developers.facebook.com/apps/';
const GUIDES = {
  'ads-mcp':
    'https://nango.dev/docs/api-integrations/meta-mcp/how-to-register-your-own-meta-mcp-oauth-app',
  'marketing-api':
    'https://nango.dev/docs/api-integrations/meta-marketing-api/how-to-register-your-own-meta-marketing-api-oauth-app',
};

/**
 * Connecting a customer-owned Meta app, in one guided flow: what Poneva may do (reporting, or ad
 * management too), preparing the business's own Meta app, who can use it, and signing in in a
 * secure window. Secrets are entered in the credential plane's window, never here.
 */
export function CustomerAppSetup({
  items,
  initialId,
  companyName,
  canCompany,
  canPrivate,
  progress,
  onClose,
  onContinue,
  onStopWaiting,
  onAsk,
}: {
  /** Each way this app can be connected (Meta: reporting, ad management). */
  items: ScopedConnectionItem[];
  initialId: string;
  companyName: string;
  canCompany: boolean;
  canPrivate: boolean;
  progress: CustomerAppProgress | null;
  onClose: () => void;
  onContinue: (target: ScopedConnectionItem, choice: ConnectionAccessChoice) => void;
  onStopWaiting: () => void;
  onAsk?: (prompt: string) => void;
}) {
  const copy = useNamespaceCopy(pageCatalog);
  const locale = useAppCatalogLocale() ?? 'en';
  const choosable = items.filter((item) => item.appSetup);
  const steps: Step[] =
    choosable.length > 1 ? ['choose', 'prepare', 'access'] : ['prepare', 'access'];
  const [step, setStep] = useState<Step>(steps[0]);
  const [selectedId, setSelectedId] = useState(initialId);
  const item = choosable.find((entry) => entry.id === selectedId) ?? choosable[0];
  const setup = item.appSetup!;
  const mcp = setup.useCase === 'ads-mcp';
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const [choice, setChoice] = useState<ConnectionAccessChoice>({
    audience: item.audience,
    people: [],
    changes: 'ask',
  });
  const people = useConnectionPeople();
  const company = useCompany().data;
  const audience = choice.audience === 'company' ? 'company' : 'private';
  const available = audience === 'company' ? canCompany : canPrivate;
  const labels: Record<Step | 'signIn', string> = {
    choose: copy.stepChoose,
    prepare: copy.stepPrepare,
    access: copy.stepAccess,
    signIn: copy.stepSignIn,
  };
  const allSteps = [...steps, 'signIn' as const];
  const current = progress ? allSteps.length - 1 : steps.indexOf(step);
  const ask = onAsk ? () => onAsk(copy.askHelpPrompt) : null;
  // The broadest way in names the setup (Meta Ads, not Meta Ads reporting) while there is a choice.
  const title =
    choosable.length > 1
      ? (choosable.find((entry) => entry.appSetup?.useCase === 'ads-mcp')?.name ?? item.name)
      : item.name;

  if (progress?.phase === 'done') {
    const readiness = progress.item.readiness;
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="pc-portal ws-meta-dialog is-done" showCloseButton={false}>
          <div className="ws-meta-done">
            <ConnectionMark
              name={progress.item.name}
              logoId={progress.item.id}
              src={null}
              state="connected"
              size="xl"
            />
            <DialogTitle>{formatCopy(copy.doneTitle, { name: progress.item.name })}</DialogTitle>
            <DialogDescription>
              {readiness?.accountCount !== undefined
                ? formatCopy(
                    progress.item.audience === 'company'
                      ? copy.doneAccountsCompany
                      : copy.doneAccountsYou,
                    {
                      count: formatCount(copy.adAccountsNoun, readiness.accountCount, locale),
                      company: companyName,
                    },
                  )
                : readiness?.toolCount !== undefined
                  ? formatCopy(copy.doneTools, {
                      count: formatCount(copy.toolsNoun, readiness.toolCount, locale),
                    })
                  : null}
            </DialogDescription>
          </div>
          {onAsk && (
            <div className="ws-meta-form-body">
              <section className="ws-meta-try">
                <h3>{copy.doneTry}</h3>
                {[copy.doneTryReport, copy.doneTryReturn].map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    className="ws-conn-prompt"
                    onClick={() => onAsk(prompt)}
                  >
                    {prompt}
                    <WorkspaceIcon name="arrow" size={15} />
                  </button>
                ))}
              </section>
            </div>
          )}
          <footer>
            <span />
            <div>
              <Button variant="outline" onClick={onClose}>
                {copy.doneDone}
              </Button>
              {onAsk && <Button onClick={() => onAsk(copy.doneTryReport)}>{copy.doneAsk}</Button>}
            </div>
          </footer>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="pc-portal ws-meta-dialog" showCloseButton={false}>
        <header>
          <div className="ws-meta-top">
            <span className="ws-meta-app">
              <ConnectionMark name={item.name} logoId={item.id} src={null} />
              <span>
                {title} · {formatCopy(copy.metaStep, { step: current + 1, total: allSteps.length })}
              </span>
            </span>
            <button
              type="button"
              className="ws-conn-icon-button"
              aria-label={copy.close}
              onClick={onClose}
            >
              <WorkspaceIcon name="close" size={17} />
            </button>
          </div>
          <ol className="ws-meta-steps" aria-label={copy.stepsLabel}>
            {allSteps.map((id, index) => (
              <li
                key={id}
                data-state={index < current ? 'done' : index === current ? 'current' : 'next'}
                aria-current={index === current ? 'step' : undefined}
              >
                {labels[id]}
              </li>
            ))}
          </ol>
          <DialogTitle>
            {progress
              ? copy.signInTitle
              : step === 'choose'
                ? copy.metaChooseTitle
                : step === 'prepare'
                  ? copy.prepareTitle
                  : copy.metaAccessTitle}
          </DialogTitle>
          <DialogDescription>
            {progress
              ? copy.signInIntro
              : step === 'choose'
                ? copy.metaChooseIntro
                : step === 'prepare'
                  ? copy.prepareIntro
                  : copy.metaAccessIntro}
          </DialogDescription>
        </header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (progress) return;
            const next = steps[steps.indexOf(step) + 1];
            if (next) setStep(next);
            else if (available && (choice.audience !== 'people' || choice.people.length))
              onContinue({ ...item, audience }, choice);
          }}
        >
          <div className="ws-meta-form-body">
            {progress ? (
              <SignInWaiting connectLink={progress.connectLink} />
            ) : step === 'choose' ? (
              <fieldset className="ws-meta-choices">
                <legend className="sr-only">{copy.metaChooseTitle}</legend>
                {choosable.map((entry) => {
                  const entryMcp = entry.appSetup?.useCase === 'ads-mcp';
                  const connected = entry.status === 'connected';
                  return (
                    <label
                      key={entry.id}
                      className="ws-meta-choice"
                      data-selected={entry.id === item.id}
                      data-disabled={connected || !entry.actions.connect.available || undefined}
                    >
                      <span className="ws-meta-choice-icon" aria-hidden="true">
                        <WorkspaceIcon name={entryMcp ? 'edit' : 'results'} size={18} />
                      </span>
                      <span className="ws-meta-choice-text">
                        <strong>
                          {entryMcp ? copy.metaManage : copy.metaReporting}
                          <small>
                            {connected
                              ? copy.metaConnectedAlready
                              : entryMcp
                                ? copy.metaManageTag
                                : copy.metaReportingTag}
                          </small>
                        </strong>
                        <span>{entryMcp ? copy.metaManageBody : copy.metaReportingBody}</span>
                      </span>
                      <input
                        type="radio"
                        name="customer-app-use"
                        checked={entry.id === item.id}
                        disabled={connected || !entry.actions.connect.available}
                        onChange={() => {
                          setSelectedId(entry.id);
                          // Reporting is shared with you or everyone and never changes anything:
                          // a choice made for ad management does not carry over to it.
                          if (!entryMcp)
                            setChoice((current) => ({
                              audience:
                                current.audience === 'people' ? 'private' : current.audience,
                              people: [],
                              changes: 'ask',
                            }));
                        }}
                      />
                    </label>
                  );
                })}
                <p className="ws-meta-note">
                  <WorkspaceIcon name="key" size={16} />
                  <span>{copy.metaOwnApp}</span>
                </p>
              </fieldset>
            ) : step === 'prepare' ? (
              <>
                <ol className="ws-meta-checklist">
                  {[
                    {
                      key: 'app',
                      title: copy.prepareAppTitle,
                      body: mcp ? copy.prepareAppManage : copy.prepareAppReporting,
                      extra: (
                        <a
                          className="ws-meta-link-button"
                          href={META_APPS_URL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {copy.prepareAppOpen}
                          <ArrowUpRight size={13} aria-hidden="true" />
                        </a>
                      ),
                    },
                    {
                      key: 'redirect',
                      title: copy.prepareRedirectTitle,
                      body: copy.prepareRedirectBody,
                      extra: (
                        <>
                          <div className="ws-meta-copy-field">
                            <input
                              id="meta-callback"
                              aria-label={copy.prepareRedirectTitle}
                              value={setup.callbackUrl}
                              readOnly
                              onFocus={(event) => event.target.select()}
                            />
                            <button
                              type="button"
                              className="ws-meta-link-button"
                              aria-label={copied ? copy.copied : copy.copyAddress}
                              onClick={() => {
                                void navigator.clipboard
                                  .writeText(setup.callbackUrl)
                                  .then(() => {
                                    setCopied(true);
                                    setCopyError(false);
                                  })
                                  .catch(() => setCopyError(true));
                              }}
                            >
                              {copied ? <Check size={13} /> : <Copy size={13} />}
                              {copied ? copy.copied : copy.copy}
                            </button>
                          </div>
                          {copyError && <p role="status">{copy.copyManual}</p>}
                        </>
                      ),
                    },
                    { key: 'secret', title: copy.prepareSecretTitle, body: copy.prepareSecretBody },
                  ].map((entry, index) => (
                    <li key={entry.key} data-done={done[entry.key] || undefined}>
                      <span className="ws-meta-check-number" aria-hidden="true">
                        {done[entry.key] ? <Check size={12} /> : index + 1}
                      </span>
                      <div>
                        <div className="ws-meta-check-row">
                          <strong id={`ws-meta-check-${entry.key}`}>{entry.title}</strong>
                          <label className="ws-meta-tick">
                            <input
                              type="checkbox"
                              // Each tick is named by its step, not only "Done".
                              aria-labelledby={`ws-meta-check-${entry.key} ws-meta-done-${entry.key}`}
                              checked={Boolean(done[entry.key])}
                              onChange={(event) =>
                                setDone({ ...done, [entry.key]: event.target.checked })
                              }
                            />
                            <span id={`ws-meta-done-${entry.key}`}>{copy.prepareDone}</span>
                          </label>
                        </div>
                        <p>{entry.body}</p>
                        {entry.extra}
                      </div>
                    </li>
                  ))}
                </ol>
                <details className="ws-meta-permissions">
                  <summary>{copy.permissionsTitle}</summary>
                  <ul>
                    {setup.scopes.map((scope) => (
                      <li key={scope}>
                        <span>{SCOPE_WORDS[scope] ? copy[SCOPE_WORDS[scope]] : scope}</span>
                        <code>{scope}</code>
                      </li>
                    ))}
                  </ul>
                  <p>{copy.permissionsNote}</p>
                </details>
                <p className="ws-meta-help">
                  <a href={GUIDES[setup.useCase]} target="_blank" rel="noreferrer">
                    {copy.guide}
                  </a>
                </p>
              </>
            ) : (
              <>
                <ConnectionPolicyFields
                  value={choice}
                  onChange={setChoice}
                  companyName={companyName}
                  canCompany={canCompany}
                  ownerUserId={company?.membership?.userId ?? null}
                  people={people}
                  allowPeople={mcp}
                  readOnly={!mcp}
                />
                {!mcp && (
                  <div className="ws-meta-fixed">
                    <span aria-hidden="true">
                      <WorkspaceIcon name="eye" size={16} />
                    </span>
                    <span>
                      <strong>{copy.metaReadOnlyTitle}</strong>
                      <small>{copy.metaReadOnlyBody}</small>
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          <footer>
            {ask ? (
              <button type="button" className="ws-text-button ws-meta-ask" onClick={ask}>
                <WorkspaceIcon name="chat" size={15} />
                {copy.askHelp}
              </button>
            ) : (
              <span />
            )}
            <div>
              {progress ? (
                <Button type="button" variant="outline" onClick={onStopWaiting}>
                  {copy.stopWaiting}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      step === steps[0] ? onClose() : setStep(steps[steps.indexOf(step) - 1])
                    }
                  >
                    {step === steps[0] ? copy.cancel : copy.back}
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      (step === 'choose' && !item.actions.connect.available) ||
                      (step === 'access' &&
                        (!available || (choice.audience === 'people' && !choice.people.length)))
                    }
                  >
                    {step === 'access' ? copy.continueToMeta : copy.continue}
                  </Button>
                </>
              )}
            </div>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SignInWaiting({ connectLink }: { connectLink: string }) {
  const copy = useNamespaceCopy(pageCatalog);
  return (
    <>
      <div className="ws-meta-window">
        <p>
          <WorkspaceIcon name="expand" size={14} />
          {copy.signInWindow}
        </p>
        <ol>
          {[copy.signInPaste, copy.signInChoose, copy.signInApprove].map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ol>
      </div>
      <div className="ws-meta-wait" role="status">
        <span className="ws-meta-live" aria-hidden="true" />
        <strong>{copy.signInWaiting}</strong>
        {connectLink && (
          <a href={connectLink} target="_blank" rel="noreferrer">
            {copy.popupOpen}
          </a>
        )}
        <span className="ws-meta-track" aria-hidden="true">
          <span />
        </span>
      </div>
      <p className="ws-meta-note">
        <WorkspaceIcon name="lock" size={16} />
        <span>{copy.signInSecret}</span>
      </p>
    </>
  );
}
